import { MessagePrimitive, useAuiState } from "@assistant-ui/react";
import { useContext, useMemo } from "react";
import { CopilotActionCard } from "@/features/copilot/components/copilot-action-card";
import { CopilotActionContext } from "@/features/copilot/components/copilot-action-context";
import { CopilotMarkdown } from "@/features/copilot/components/copilot-markdown";
import { CopilotPartRoleContext } from "@/features/copilot/components/copilot-part-role-context";
import {
  type CopilotPartRole,
  planCopilotMessageParts,
} from "@/features/copilot/lib/copilot-message-plan";

const PART_COMPONENTS = { Text: CopilotMarkdown, tools: { Fallback: CopilotActionCard } };

interface PartBlock {
  process: boolean;
  indexes: number[];
}

/// Narration and lookup rows that sit next to each other are one stretch of work, so they are
/// drawn as a single block; everything else stands on its own.
function processBlocks(roles: readonly CopilotPartRole[]): PartBlock[] {
  const blocks: PartBlock[] = [];
  roles.forEach((role, index) => {
    if (role === "hidden") return;
    const process = role === "interim" || role === "row";
    const last = blocks.at(-1);
    if (process && last?.process) last.indexes.push(index);
    else blocks.push({ process, indexes: [index] });
  });
  return blocks;
}

function PlannedPart({ index, role }: { index: number; role: CopilotPartRole }) {
  return (
    <CopilotPartRoleContext.Provider value={role}>
      <MessagePrimitive.PartByIndex index={index} components={PART_COMPONENTS} />
    </CopilotPartRoleContext.Provider>
  );
}

/// Parts are rendered one by one so each gets the role the plan gave it: the answer reads as the
/// answer, narration before a later step is muted, and retries and planning steps never show.
export function CopilotMessage() {
  const role = useAuiState((state) => state.message.role);
  const parts = useAuiState((state) => state.message.parts);
  const actions = useContext(CopilotActionContext)?.actions;
  const roles = useMemo(
    () => planCopilotMessageParts(parts, (toolCallId) => actions?.has(toolCallId) === true),
    [actions, parts],
  );

  return (
    <MessagePrimitive.Root
      className={
        role === "user" ? "ml-auto max-w-[88%] rounded-xl bg-muted px-3 py-2" : "max-w-full py-2"
      }
    >
      {processBlocks(roles).map((block) =>
        block.process ? (
          <div key={block.indexes[0]} className="my-2 space-y-1 border-l border-border pl-3">
            {block.indexes.map((index) => (
              <PlannedPart key={index} index={index} role={roles[index] ?? "answer"} />
            ))}
          </div>
        ) : (
          <PlannedPart
            key={block.indexes[0]}
            index={block.indexes[0] ?? 0}
            role={roles[block.indexes[0] ?? 0] ?? "answer"}
          />
        ),
      )}
      <MessagePrimitive.Error>
        <p role="alert" className="mt-2 text-sm text-destructive">
          The answer could not finish. You can retry with a new message.
        </p>
      </MessagePrimitive.Error>
    </MessagePrimitive.Root>
  );
}
